import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, Pencil, Trash2, X, ChevronDown, ChevronUp, Building2, ShieldCheck,
} from 'lucide-react'
import {
  backofficeApi,
  type BackofficeOrg,
  type BackofficeCreateOrgPayload,
  type BackofficeUpdateOrgPayload,
} from '../../lib/api'

const PLAN_COLORS: Record<string, string> = {
  starter: 'bg-slate-700 text-slate-300',
  professional: 'bg-blue-900/50 text-blue-300',
  enterprise: 'bg-emerald-900/50 text-emerald-300',
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-emerald-900/50 text-emerald-300',
  trialing: 'bg-yellow-900/50 text-yellow-300',
  canceled: 'bg-red-900/50 text-red-300',
  past_due: 'bg-orange-900/50 text-orange-300',
  unpaid: 'bg-red-900/50 text-red-300',
  paused: 'bg-slate-700 text-slate-300',
}

const PLANS = ['starter', 'professional', 'enterprise']

// ---------------------------------------------------------------------------
// Create / Edit modal
// ---------------------------------------------------------------------------

interface OrgModalProps {
  initial?: BackofficeOrg | null
  onClose: () => void
  onSave: (payload: BackofficeCreateOrgPayload | BackofficeUpdateOrgPayload) => void
  saving: boolean
}

function OrgModal({ initial, onClose, onSave, saving }: OrgModalProps) {
  const [name, setName] = useState(initial?.name ?? '')
  const [industry, setIndustry] = useState(initial?.industry ?? '')
  const [website, setWebsite] = useState(initial?.website ?? '')
  const [plan, setPlan] = useState(initial?.plan ?? 'starter')
  const [planOverride, setPlanOverride] = useState<string>(initial?.plan_override ?? '')
  const [budget, setBudget] = useState<string>(String(initial?.monthly_budget_limit ?? 50))

  const isEdit = !!initial

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const payload: Record<string, unknown> = {
      name,
      plan,
      monthly_budget_limit: parseFloat(budget) || 50,
    }
    if (industry) payload.industry = industry
    if (website) payload.website = website
    if (isEdit) {
      payload.plan_override = planOverride || null
    }
    onSave(payload as BackofficeCreateOrgPayload | BackofficeUpdateOrgPayload)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <h2 className="text-sm font-semibold text-slate-100">
            {isEdit ? 'Edit organization' : 'New organization'}
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Name *</label>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Industry</label>
              <input
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="e.g. Consulting"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Website</label>
              <input
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Plan</label>
              <select
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-500"
              >
                {PLANS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Monthly budget (USD)</label>
              <input
                type="number"
                min="0"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-500"
              />
            </div>
          </div>
          {isEdit && (
            <div>
              <label className="block text-xs text-slate-400 mb-1">
                Plan override{' '}
                <span className="text-slate-500">(locks plan, bypasses Stripe — leave blank to remove)</span>
              </label>
              <select
                value={planOverride}
                onChange={(e) => setPlanOverride(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-500"
              >
                <option value="">No override</option>
                {PLANS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-400 hover:text-slate-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create organization'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function BackofficeOrgsPage() {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<BackofficeOrg | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<BackofficeOrg | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const { data: orgs = [], isLoading } = useQuery<BackofficeOrg[]>({
    queryKey: ['backoffice', 'orgs'],
    queryFn: backofficeApi.listOrgs,
  })

  const { data: expandedOrg, isLoading: loadingExpanded } = useQuery<BackofficeOrg>({
    queryKey: ['backoffice', 'org', expandedId],
    queryFn: () => backofficeApi.getOrg(expandedId!),
    enabled: !!expandedId,
  })

  const createMutation = useMutation({
    mutationFn: (payload: BackofficeCreateOrgPayload) => backofficeApi.createOrg(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['backoffice', 'orgs'] })
      qc.invalidateQueries({ queryKey: ['backoffice', 'stats'] })
      setCreateOpen(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: BackofficeUpdateOrgPayload }) =>
      backofficeApi.updateOrg(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['backoffice', 'orgs'] })
      qc.invalidateQueries({ queryKey: ['backoffice', 'org', editTarget?.id] })
      setEditTarget(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => backofficeApi.deleteOrg(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['backoffice', 'orgs'] })
      qc.invalidateQueries({ queryKey: ['backoffice', 'stats'] })
      setDeleteTarget(null)
    },
  })

  const filtered = orgs.filter((o) =>
    o.name.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-100">Organizations</h1>
          <p className="text-sm text-slate-500 mt-0.5">{orgs.length} total</p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          New org
        </button>
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Search by name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full max-w-xs bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500"
      />

      {/* Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-emerald-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-500 text-sm">No organizations found.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-400">Name</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-400">Plan</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-400">Status</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-400">Users</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-400">Period end</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((org) => {
                const isExpanded = expandedId === org.id
                return (
                  <>
                    <tr
                      key={org.id}
                      className="border-b border-slate-800 hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-500 flex-shrink-0" />
                          <span className="text-sm text-slate-100 font-medium">{org.name}</span>
                          {org.plan_override && (
                            <span title="Plan override active"><ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /></span>
                          )}
                        </div>
                        {org.industry && (
                          <p className="text-xs text-slate-500 mt-0.5 pl-6">{org.industry}</p>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full ${PLAN_COLORS[org.plan] ?? 'bg-slate-700 text-slate-300'}`}
                        >
                          {org.plan}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[org.subscription_status] ?? 'bg-slate-700 text-slate-300'}`}
                        >
                          {org.subscription_status}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-sm text-slate-400">
                        {org.user_count ?? '—'}
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-500">
                        {org.current_period_end
                          ? new Date(org.current_period_end).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={() => setExpandedId(isExpanded ? null : org.id)}
                            className="p-1.5 text-slate-500 hover:text-slate-300 transition-colors"
                            title="Members"
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => setEditTarget(org)}
                            className="p-1.5 text-slate-500 hover:text-emerald-400 transition-colors"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(org)}
                            className="p-1.5 text-slate-500 hover:text-red-400 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded members row */}
                    {isExpanded && (
                      <tr key={`${org.id}-expanded`} className="border-b border-slate-800 bg-slate-800/30">
                        <td colSpan={6} className="px-8 py-3">
                          {loadingExpanded ? (
                            <div className="text-xs text-slate-500">Loading members…</div>
                          ) : (expandedOrg?.members?.length ?? 0) === 0 ? (
                            <div className="text-xs text-slate-500">No members.</div>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {expandedOrg?.members?.map((m) => (
                                <span
                                  key={m.id}
                                  className="flex items-center gap-1.5 text-xs bg-slate-800 border border-slate-700 rounded-full px-3 py-1 text-slate-300"
                                >
                                  <span className="font-medium">{m.full_name ?? m.email}</span>
                                  <span className="text-slate-500">·</span>
                                  <span className="text-slate-500">{m.role}</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create modal */}
      {createOpen && (
        <OrgModal
          onClose={() => setCreateOpen(false)}
          onSave={(payload) =>
            createMutation.mutate(payload as BackofficeCreateOrgPayload)
          }
          saving={createMutation.isPending}
        />
      )}

      {/* Edit modal */}
      {editTarget && (
        <OrgModal
          initial={editTarget}
          onClose={() => setEditTarget(null)}
          onSave={(payload) =>
            updateMutation.mutate({
              id: editTarget.id,
              payload: payload as BackofficeUpdateOrgPayload,
            })
          }
          saving={updateMutation.isPending}
        />
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-sm p-6 space-y-4">
            <h2 className="text-sm font-semibold text-slate-100">Delete organization</h2>
            <p className="text-sm text-slate-400">
              Are you sure you want to delete{' '}
              <span className="text-slate-200 font-medium">{deleteTarget.name}</span>?
              This is irreversible and will remove all associated data.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm text-slate-400 hover:text-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteTarget.id)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
